import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../api/config';
import { doc, getDoc } from 'backend/database';
import { getClientByUserId } from '../api/patientsAPI';
import { onAuthStateChanged } from 'backend/auth';
import { db, auth } from '../backend/config';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [licenseActive, setLicenseActive] = useState(true);
  const [institutionId, setInstitutionId] = useState(null);
  const [institutionData, setInstitutionData] = useState(null);
  const logoutTimeoutRef = useRef(null);

  useEffect(() => {
    // Check for existing token and user on mount
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    // Optimistically set user from localStorage for instant feel
    if (token && storedUser && token !== 'dev-token') {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setUserProfile(userData);
        const role = userData.userType || userData.type || userData.role || 'student';
        setUserRole(role);
        setUserRoles(userData.roles || [role]);
        if (userData.institutionId) {
          setInstitutionId(userData.institutionId);
        }
      } catch (e) {
        console.warn('Failed to parse cached user:', e);
      }
    }

    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        // Use the validated user object
        const userData = authUser;
        setUser(userData);
        setUserProfile(userData);
        const role = userData.userType || userData.type || userData.role || 'student';
        setUserRole(role);
        setUserRoles(userData.roles || [role]);
        if (userData.institutionId) {
          setInstitutionId(userData.institutionId);
        }

        // Fetch extra fields (medicalConditions etc) in the background if needed.
        // Prefer the backend row id (uuid) — client queries filter on user_id
        // which never matches a firebase uid.
        const userId = userData.id || userData.uid;
        if (userId && !window.location.pathname.startsWith('/login')) {
          // Use cached results if available via getDoc (database.js handles cache)
          Promise.all([
            getDoc(doc(db, 'users', userId)),
            getClientByUserId(userId).catch(() => null)
          ]).then(([userDoc, clientData]) => {
            if (userDoc.exists()) {
              const dbProfile = userDoc.data();
              const mergedProfile = { ...userData, ...dbProfile, ...(clientData || {}) };
              // Never let the merged client row overwrite the ACCOUNT identity:
              // userProfile.id must stay the users.id that conversations,
              // notifications, calls and scoping key on. The client record id
              // is exposed separately as clientId.
              mergedProfile.id = userData.id || dbProfile.id;
              mergedProfile.uid = userData.uid || mergedProfile.id;
              if (clientData?.id) mergedProfile.clientId = clientData.id;
              setUserProfile(mergedProfile);
              setUser(mergedProfile);
              localStorage.setItem('user', JSON.stringify(mergedProfile));
            }
          }).catch(() => {});
        }
      } else {
        // Session invalid or missing
        setUser(null);
        setUserProfile(null);
        setUserRole(null);
        setUserRoles([]);
        setInstitutionId(null);
        setInstitutionData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch institution (tenant) data whenever institutionId changes so dashboards
  // can display the actual tenant name instead of a generic "Institution" label.
  useEffect(() => {
    if (!institutionId || institutionData?.id === institutionId) {
      if (!institutionId) setInstitutionData(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'institutions', institutionId))
      .then((instDoc) => {
        if (cancelled) return;
        if (instDoc.exists()) {
          setInstitutionData({ id: instDoc.id, ...instDoc.data() });
        } else {
          // Try fetching from the institutions table via the data API as a fallback
          return fetch(`${api.defaults?.baseURL || ''}/api/data/institutions/${institutionId}`, {
            credentials: 'include',
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((json) => {
              if (!cancelled && json && json.success && json.data) {
                setInstitutionData(json.data);
              }
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        console.error('Failed to fetch institution data:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [institutionId]);

  const login = async (credentials) => {
    try {
      const response = await api.post('/api/auth/login', credentials);
      
      if (response.data.success) {
        const { token, user: userData } = response.data.data;
        
        // Store token and user data
        if (token) localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Update state
        setUser(userData);
        setUserProfile(userData);
        
        const role = userData.userType || userData.type || userData.role || 'student';
        setUserRole(role);
        setUserRoles(userData.roles || [role]);
        
        if (userData.institutionId) {
          setInstitutionId(userData.institutionId);
        }
        
        return { success: true, user: userData };
      }
      
      return { success: false, message: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      // Call backend logout to invalidate the session server-side.
      // On web the JWT is an httpOnly cookie — credentials:'include' is what
      // carries it, so the call must run even when no localStorage token exists.
      const token = localStorage.getItem('token');
      try {
        const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        });
      } catch (e) {
        // Best-effort: even if the server call fails, clear local state
        console.warn('Server logout failed (clearing local state anyway):', e);
      }

      // Clear local storage (must clear all token keys)
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Clear state
      setUser(null);
      setUserProfile(null);
      setUserRole(null);
      setUserRoles([]);
      setInstitutionId(null);
      setInstitutionData(null);

      // Clear logout timeout
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateUserProfile = (updates) => {
    setUserProfile(prev => ({ ...prev, ...updates }));
    setUser(prev => ({ ...prev, ...updates }));

    // Update localStorage
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = { ...currentUser, ...updates };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUserProfile = async () => {
    const userId = user?.id || user?.uid;
    if (!userId) return;
    try {
      const [userDoc, clientData] = await Promise.all([
        getDoc(doc(db, 'users', userId)),
        getClientByUserId(userId)
      ]);
      
      if (userDoc.exists()) {
        const dbProfile = userDoc.data();
        // Merge client record over user record so client-specific fields
        // (dateOfBirth, emergencyContact, medicalConditions) are available
        const mergedProfile = { ...userProfile, ...dbProfile, ...(clientData || {}) };
        setUserProfile(mergedProfile);
        setUser(mergedProfile);
        localStorage.setItem('user', JSON.stringify(mergedProfile));
        const dbRole = dbProfile.userType || dbProfile.type || dbProfile.role || userRole;
        setUserRole(dbRole);
        if (dbProfile.institutionId) {
          setInstitutionId(dbProfile.institutionId);
        }
      }
    } catch (err) {
      console.error('Failed to refresh user profile:', err);
    }
  };

  const isDoctor = () => userRole === 'doctor';
  const isCaregiver = () => userRole === 'caregiver';
  const isPharmacist = () => userRole === 'pharmacist';
  const isNurse = () => userRole === 'nurse';
  const isElderly = () => userRole === 'elderly';
  const isAdmin = () => userRole === 'admin';
  const isServiceProvider = () => ['caregiver', 'nurse', 'doctor', 'pharmacist'].includes(userRole);

  const getCaregiverOnboardingRoute = () => {
    // Institution caregivers use the institution onboarding flow
    if (institutionId || userProfile?.institutionId) {
      const instId = institutionId || userProfile?.institutionId;
      return `/institution-caregiver/onboarding?institution=${instId}`;
    }
    return '/caregiver/onboarding';
  };

  const isOnboardingIncomplete = () => {
    if (!userProfile) {
      console.log('No user profile found, onboarding required');
      return true;
    }

    console.log('Checking onboarding completion:', {
      userRole,
      userType: userProfile.userType,
      onboardingComplete: userProfile.onboardingComplete,
      status: userProfile.status
    });

    const caregiverTypes = ['caregiver', 'nurse', 'doctor', 'pharmacist'];
    if (caregiverTypes.includes(userProfile.userType)) {
      // If user is activated, allow access regardless of onboarding status
      if (userProfile.status === 'active' && userProfile.onboardingComplete === true) {
        console.log(`✅ ${userProfile.userType} onboarding complete`);
        return false;
      }
      const isComplete = userProfile.onboardingComplete === true;
      if (!isComplete) {
        console.log(`🚫 ${userProfile.userType?.toUpperCase()} ONBOARDING INCOMPLETE - blocking access`);
        return true;
      }
      console.log(`✅ ${userProfile.userType} onboarding complete`);
      return false;
    }

    if (userRole === 'admin' || userProfile.userType === 'admin') {
      console.log('✅ Admin access - no onboarding required');
      return false;
    }

    if (userProfile.userType === 'elderly' || userRole === 'Client' || userProfile.userType === 'client' || userProfile.userType === 'patient') {
      if (!userProfile.onboardingProfileComplete && !userProfile.onboardingComplete) {
        console.log('🚫 Client ONBOARDING INCOMPLETE - blocking access');
        return true;
      }
      console.log('✅ Client onboarding complete');
      return false;
    }

    console.log('⚠️ Unknown user type, requiring onboarding');
    return true;
  };

  const value = {
    user,
    userProfile,
    loading,
    userRole,
    userRoles,
    licenseActive,
    institutionId,
    institutionData,
    login,
    logout,
    updateUserProfile,
    setLicenseActive,
    setInstitutionData,
    logoutTimeoutRef,
    isOnboardingIncomplete,
    getCaregiverOnboardingRoute,
    isServiceProvider,
    isDoctor,
    isCaregiver,
    isPharmacist,
    isNurse,
    isElderly,
    isAdmin,
    refreshUserProfile
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
